import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roles';
import { WO_TYPES, WO_TYPE_LABELS, WO_TYPE_COLORS, PRIORITIES, FREQUENCIES } from '../utils/constants';
import { FiArrowLeft, FiUpload, FiTrash2, FiSearch, FiX, FiCheck, FiPlus, FiLayers } from 'react-icons/fi';
import UploadProgress from '../components/UploadProgress';
import titleCase from '../utils/titleCase';
import { playSuccess, playError } from '../utils/sounds';
import { useTheme } from '../context/ThemeContext';


export default function CreateNotification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, authFetch, token } = useAuth();
  const { isDark } = useTheme();


  // Requester picks a rough timeframe instead of an exact date — the
  // supervisor picks the exact planned date at approval time, defaulting
  // to the week implied by this bucket.
  const RANGE_BUCKETS = [
    { id: 'this_week', label: 'This Working Week' },
    { id: '2w', label: 'Within 2 Weeks' },
    { id: '4w', label: 'Within 4 Weeks' },
    { id: '8w', label: 'Within 8 Weeks' },
    { id: '12w_plus', label: '12 Weeks+' },
  ];
  const computeRangeDate = (bucket) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (bucket === 'this_week') {
      const dow = today.getDay(); // 0=Sun..6=Sat — week ends Sunday
      const daysToSunday = dow === 0 ? 0 : 7 - dow;
      const end = new Date(today); end.setDate(today.getDate() + daysToSunday);
      return fmt(end);
    }
    const daysByBucket = { '2w': 14, '4w': 28, '8w': 56, '12w_plus': 84 };
    const d = new Date(today); d.setDate(today.getDate() + (daysByBucket[bucket] || 0));
    return fmt(d);
  };

  const preselectedAssetId = searchParams.get('asset');
  const preselectedType = searchParams.get('type');
  const parentWoId = searchParams.get('parent');
  const parentWoNumber = searchParams.get('parentNumber');
  const parentWoName = searchParams.get('parentName');
  const parentWorkOrder = parentWoId ? { id: parentWoId, wo_number: parentWoNumber, name: parentWoName } : null;

  const [form, setForm] = useState({
    type: preselectedType || 'PM01',
    name: '',
    description: '',
    asset_id: preselectedAssetId || '',
    priority: 'Normal',
    assigned_to: '',
    due_date: '',
    frequency: '1 Month',
    status: 'New',
    estimated_hours: '',
    estimated_minutes: ''
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successResult, setSuccessResult] = useState(null);

  const [assets, setAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetName, setSelectedAssetName] = useState('');
  const [users, setUsers] = useState([]);
  const [supervisorName, setSupervisorName] = useState(null);
  // Functional-location drill-down filter for the asset picker.
  const [flNodes, setFlNodes] = useState([]);
  const [flFilter, setFlFilter] = useState('');   // selected FL id (empty = no filter)

  // PM02 templates use a single operation for the entire checklist
  const PM02_DEFAULT_OPERATION = '0010';
  const [taskListTemplates, setTaskListTemplates] = useState([]);
  const [selectedTaskList, setSelectedTaskList] = useState(null);
  const opIdRef = useRef(1);
  const [plannedOps, setPlannedOps] = useState([{ _id: 0, title: '', est_hours: 0, est_minutes: 0, resources: 1, isDefault: true }]);
  const [showAddOp, setShowAddOp] = useState(false);
  const [newOp, setNewOp] = useState({ title: '', est_hours: 0, est_minutes: 0, resources: 1 });
  const [dateRange, setDateRange] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    authFetch('/api/users').then(r => r.json()).then(setUsers).catch(() => {});
    authFetch('/api/notifs/my-supervisor').then(r => r.json()).then(data => {
      if (data.supervisor_name) setSupervisorName(data.supervisor_name);
    }).catch(() => {});
    if (isAdmin(user)) {
      authFetch('/api/issues/task-list-templates/all').then(r => r.json()).then(d => setTaskListTemplates(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, []);

  useEffect(() => {
    authFetch('/api/assets?limit=200').then(r => r.json()).then(res => {
      const data = res && res.data ? res.data : (Array.isArray(res) ? res : []);
      setAssets(data);
      if (preselectedAssetId) {
        const a = data.find(a => a.id === parseInt(preselectedAssetId));
        if (a) setSelectedAssetName(`${a.tag} - ${a.name}`);
      }
    }).catch(() => {});
    // FL tree for the drill-down filter. Quietly absent on pre-v103 tenants.
    authFetch('/api/functional-locations').then(r => r.ok ? r.json() : []).then(d => setFlNodes(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Subtree ids for the currently selected FL filter — used to restrict the
  // visible assets when a parent node is picked (so 5061 also matches assets
  // under 5061.01.01 etc.). Built once per flFilter change.
  const flFilterSubtree = (() => {
    if (!flFilter) return null;
    const startId = parseInt(flFilter);
    if (Number.isNaN(startId)) return null;
    const ids = new Set([startId]);
    const stack = [startId];
    while (stack.length) {
      const cur = stack.pop();
      for (const n of flNodes) {
        if (n.parent_id === cur && !ids.has(n.id)) { ids.add(n.id); stack.push(n.id); }
      }
    }
    return ids;
  })();



  const filteredAssets = assets.filter(a => {
    if ((a.status_name || '').toLowerCase() === 'redundant') return false;
    // FL drill-down filter — match the picked node OR any descendant
    if (flFilterSubtree && !flFilterSubtree.has(a.functional_location_id)) return false;
    if (!assetSearch) return true;
    const q = assetSearch.toLowerCase();
    return a.name.toLowerCase().includes(q) || (a.tag || '').toLowerCase().includes(q);
  });

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).slice(0, 8 - files.length);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.asset_id) { setError('Asset is required'); return; }
    if (!dateRange) { setError('Please choose when you need this done'); return; }
    // Type is driven by the chosen timeframe, not an exact date — "this
    // working week" is treated as a same-day/urgent break-in.
    const autoType = dateRange === 'this_week' ? 'PM03' : 'PM01';
    const rangeDate = computeRangeDate(dateRange);
    // Validate all operations have required fields
    for (let i = 0; i < plannedOps.length; i++) {
      const op = plannedOps[i];
      if (!op.title.trim()) { setError(`Operation ${String((i + 1) * 10).padStart(4, '0')} requires a name`); return; }
    }

    setSaving(true);
    setError('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('description', form.description);
      formData.append('asset_id', form.asset_id);
      formData.append('type', autoType);
      formData.append('priority', form.priority);
      formData.append('status', autoType === 'PM03' ? 'In Progress' : 'New');
      formData.append('due_date', rangeDate);
      formData.append('assigned_to', form.assigned_to || user.id);
      const estHours = parseFloat(form.estimated_hours) || 0;
      const estMinutes = parseInt(form.estimated_minutes) || 0;
      const totalEstimated = estHours + (estMinutes / 60);
      if (totalEstimated > 0) formData.append('estimated_hours', totalEstimated);
      if (parentWorkOrder) formData.append('parent_wo_id', parentWorkOrder.id);
      if (plannedOps.length > 0) {
        const serializedOps = plannedOps.map(op => ({
          title: op.title.trim(),
          estimated_hours: (op.est_hours || 0) + (op.est_minutes || 0) / 60,
          planned_date: rangeDate,
          resources: op.resources || 1
        }));
        formData.append('planned_operations', JSON.stringify(serializedOps));
      }
      files.forEach(f => formData.append('files', f));

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/notifs');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try { reject(new Error(JSON.parse(xhr.responseText).error)); }
            catch { reject(new Error('Failed to create notification')); }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      playSuccess();
      setSuccessResult(result);
    } catch (err) {
      setError(err.message);
      playError();
    } finally {
      setSaving(false);
    }
  };

  const handleTaskListSelect = (tplId) => {
    if (!tplId) {
      setSelectedTaskList(null);
      setForm(f => ({ ...f, description: '', estimated_hours: '', estimated_minutes: '', frequency: '1 Month' }));
      setPlannedOps([{ _id: opIdRef.current++, title: '', est_hours: 0, est_minutes: 0, resources: 1, isDefault: true }]);
      return;
    }
    const tpl = taskListTemplates.find(t => t.id === parseInt(tplId));
    if (tpl) {
      setSelectedTaskList(tpl);
      const tplHours = tpl.planned_hours || 0;
      const hours = Math.floor(tplHours);
      const mins = Math.round((tplHours - hours) * 60);
      const nearestMins = [0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - mins) < Math.abs(prev - mins) ? curr : prev);
      setForm(f => ({
        ...f, description: tpl.description || '',
        estimated_hours: hours > 0 ? String(hours) : '',
        estimated_minutes: nearestMins > 0 ? String(nearestMins) : '',
        frequency: tpl.schedule_frequency || f.frequency,
        priority: tpl.priority || f.priority
      }));
      // PM02 templates use a single operation (0010) with template name for the entire checklist
      setPlannedOps([{
        _id: opIdRef.current++,
        title: tpl.name || '',
        est_hours: hours,
        est_minutes: nearestMins,
        resources: tpl.planned_resources || 1,
        isDefault: true,
        fromTemplate: true
      }]);
    }
  };

  const titlePreview = form.type === 'PM02' && form.name
    ? `${form.name.trim()} (${selectedTaskList?.schedule_frequency || form.frequency})`
    : null;

  const notifTypes = [
    { value: 'PM03', label: 'Breakdown', desc: 'Broken / urgent / same day', color: '#f59e0b' },
    { value: 'PM01', label: 'Planned', desc: 'Scheduled maintenance', color: '#2563eb' },
  ];

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
    background: 'var(--bg-input)', color: 'var(--text-primary)'
  };

  if (successResult) {
    return (
      <div style={{ padding: '24px 16px' }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
          }}>
            <FiCheck size={32} color="#10b981" strokeWidth={2.5} />
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Notification Created</h3>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb', marginBottom: 12, fontFamily: 'monospace', letterSpacing: 1 }}>
            {successResult.notif_number}
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
            Your notification has been submitted{supervisorName ? ` to ${supervisorName}` : ''} for supervisor approval.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => navigate(-1)}
              style={{
                padding: '12px 28px', borderRadius: 10, border: '1px solid var(--border)',
                background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, cursor: 'pointer'
              }}>
              Go Back
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                setSuccessResult(null);
                setForm({ type: preselectedType || 'PM01', name: '', description: '', asset_id: preselectedAssetId || '', priority: 'Normal', assigned_to: '', due_date: '', frequency: '1 Month', status: 'New', estimated_hours: '', estimated_minutes: '' });
                setFiles([]);
                setPlannedOps([{ _id: opIdRef.current++, title: '', est_hours: 0, est_minutes: 0, resources: 1, isDefault: true }]);
                setSelectedTaskList(null);
                setDateRange('');
              }}
              style={{
                padding: '12px 28px', borderRadius: 10, border: 'none',
                background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
              }}>
              Create Another
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 200 }}
      onClick={(e) => { if (e.target === e.currentTarget) navigate(-1); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              {parentWorkOrder ? 'New Sub Notification' : 'New Notification'}
            </h2>
            {supervisorName && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Will be sent to {supervisorName} for approval
              </div>
            )}
          </div>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
        </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '16px 20px 20px', overflowY: 'auto', flex: 1, overscrollBehavior: 'contain' }}>
        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', color: '#ef4444',
            fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,0.2)'
          }}>{error}</div>
        )}

        {parentWorkOrder && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.08)',
            border: '1px solid rgba(99,102,241,0.2)', marginBottom: 16, fontSize: 13, color: '#6366f1'
          }}>
            <span style={{ fontWeight: 600 }}>Sub notification of:</span> {parentWorkOrder.wo_number} — {parentWorkOrder.name}
          </div>
        )}

        {/* When do you need this done — replaces asking for an exact date.
            The supervisor picks the actual date at approval time. */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
            When do you need this done? <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {RANGE_BUCKETS.map(b => {
              const selected = dateRange === b.id;
              return (
                <button key={b.id} type="button" onClick={() => setDateRange(b.id)}
                  style={{
                    padding: '7px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${selected ? '#2563eb' : 'var(--border)'}`,
                    background: selected ? '#2563eb' : 'var(--bg-input)',
                    color: selected ? '#fff' : 'var(--text-secondary)',
                  }}>
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Auto-determined type indicator */}
        {dateRange && (() => {
          const autoType = dateRange === 'this_week' ? 'PM03' : 'PM01';
          const typeInfo = autoType === 'PM03'
            ? { label: 'Breakdown', desc: 'Same-day urgent work', color: '#f59e0b' }
            : { label: 'Planned', desc: 'Scheduled maintenance', color: '#2563eb' };
          return (
            <div style={{ padding: '8px 14px', borderRadius: 8, background: `${typeInfo.color}10`, border: `1px solid ${typeInfo.color}30`, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: typeInfo.color }}>{autoType}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{typeInfo.label} — {typeInfo.desc}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>Based on timeframe chosen</div>
            </div>
          );
        })()}

        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Title *</label>
          <input type="text" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: titleCase(e.target.value) }))}
            placeholder="Notification title"
            style={inputStyle}
          />
          {titlePreview && (
            <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4, fontStyle: 'italic' }}>
              Will be saved as: "{titlePreview}"
            </div>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Description</label>
          <textarea value={form.description}
            onChange={e => {
              let v = e.target.value;
              if (v.length > 0) v = v.charAt(0).toUpperCase() + v.slice(1);
              v = v.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
              setForm(f => ({ ...f, description: v }));
            }}
            // Auto-grow: reset height to 'auto' then to scrollHeight so the
            // textarea matches the content size as the user types. No inner
            // scrollbar; vertical-only manual resize handle still available.
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            placeholder="Describe the work to be done..."
            rows={3}
            disabled={!!selectedTaskList}
            style={{
              ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5, overflow: 'hidden',
              background: selectedTaskList ? 'var(--bg-secondary)' : 'var(--bg-input)',
              opacity: selectedTaskList ? 0.7 : 1, cursor: selectedTaskList ? 'not-allowed' : 'text'
            }}
          />
        </div>

        {/* Asset Search */}
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Asset <span style={{ color: '#dc2626' }}>*</span></label>
          {/* Optional FL drill-down — narrow the asset search to a site/area first */}
          {flNodes.length > 0 && (
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter by location</span>
              <select value={flFilter} onChange={e => { setFlFilter(e.target.value); setForm(f => ({ ...f, asset_id: '' })); setSelectedAssetName(''); }}
                style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                <option value="">All locations</option>
                {flNodes.slice().sort((a, b) => a.code.localeCompare(b.code)).map(n => {
                  // Depth = count of ancestors, for indentation
                  let depth = 0; let cur = n;
                  while (cur && cur.parent_id != null) { const p = flNodes.find(x => x.id === cur.parent_id); if (!p) break; depth++; cur = p; }
                  return <option key={n.id} value={n.id}>{`${'  '.repeat(depth)}${n.code} — ${n.name}`}</option>;
                })}
              </select>
              {flFilter && (
                <button type="button" onClick={() => setFlFilter('')} title="Clear location filter"
                  style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <FiX size={12} />
                </button>
              )}
            </div>
          )}
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input type="text"
              value={form.asset_id ? selectedAssetName : assetSearch}
              onChange={e => {
                setAssetSearch(e.target.value);
                setForm(f => ({ ...f, asset_id: '' }));
                setSelectedAssetName('');
                setShowAssetDropdown(true);
              }}
              onFocus={() => setShowAssetDropdown(true)}
              placeholder="Search assets..."
              style={{ ...inputStyle, paddingLeft: 32 }}
            />
            {form.asset_id && (
              <button type="button" onClick={() => { setForm(f => ({ ...f, asset_id: '' })); setSelectedAssetName(''); setAssetSearch(''); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <FiX size={14} />
              </button>
            )}
          </div>
          {showAssetDropdown && !form.asset_id && (
            <div style={{
              position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 10,
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 280,
              overflow: 'auto', overscrollBehavior: 'contain', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: 4
            }}>
              {filteredAssets.slice(0, 30).map(a => (
                <div key={a.id}
                  onPointerDown={e => { e.preventDefault(); setForm(f => ({ ...f, asset_id: a.id })); setSelectedAssetName(`${a.tag} - ${a.name}`); setShowAssetDropdown(false); }}
                  style={{
                    padding: '11px 12px', cursor: 'pointer', fontSize: 13,
                    borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.1s',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{a.tag}</span>
                    <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>- {a.name}</span>
                    {a.functional_location_code && (
                      <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.10)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.25)' }}>{a.functional_location_code}</span>
                    )}
                  </div>
                </div>
              ))}
              {filteredAssets.length === 0 && (
                <div style={{ padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No assets found</div>
              )}
            </div>
          )}
        </div>

        {/* Planned Operations */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
            <FiLayers size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Operations {plannedOps.length > 0 && `(${plannedOps.length})`}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {plannedOps.map((op, idx) => (
              <div key={op._id} style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                    {String((idx + 1) * 10).padStart(4, '0')}
                  </span>
                  {!op.isDefault && (
                    <button onClick={() => setPlannedOps(prev => prev.filter((_, i) => i !== idx))}
                      style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: '#dc2626', fontSize: 10 }}>
                      <FiTrash2 size={12} />
                    </button>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Operation Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input value={op.title} onChange={e => { const v = titleCase(e.target.value); setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, title: v } : o)); }}
                    placeholder="Operation name..."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Est. Hours</label>
                    <select value={op.est_hours || 0} onChange={e => setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, est_hours: parseInt(e.target.value) } : o))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}>
                      {Array.from({ length: 25 }, (_, i) => <option key={i} value={i}>{i}h</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Est. Minutes</label>
                    <select value={op.est_minutes || 0} onChange={e => setPlannedOps(prev => prev.map((o, i) => i === idx ? { ...o, est_minutes: parseInt(e.target.value) } : o))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}>
                      {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m}m</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Hide Add Operation when PM02 template is selected (single operation only) */}
          {!selectedTaskList && (showAddOp ? (
            <div style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Operation Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input value={newOp.title} onChange={e => setNewOp(o => ({ ...o, title: titleCase(e.target.value) }))} autoFocus placeholder="Operation name..."
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Est. Hours</label>
                  <select value={newOp.est_hours || 0} onChange={e => setNewOp(o => ({ ...o, est_hours: parseInt(e.target.value) }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}>
                    {Array.from({ length: 25 }, (_, i) => <option key={i} value={i}>{i}h</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Est. Minutes</label>
                  <select value={newOp.est_minutes || 0} onChange={e => setNewOp(o => ({ ...o, est_minutes: parseInt(e.target.value) }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', boxSizing: 'border-box' }}>
                    {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m}m</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowAddOp(false); setNewOp({ title: '', est_hours: 0, est_minutes: 0, resources: 1 }); }}
                  style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                  Cancel
                </button>
                <button onClick={() => {
                  if (!newOp.title.trim()) return;
                  setPlannedOps(prev => [...prev, { _id: opIdRef.current++, title: newOp.title.trim(), est_hours: newOp.est_hours || 0, est_minutes: newOp.est_minutes || 0, estimated_hours: (newOp.est_hours || 0) + (newOp.est_minutes || 0) / 60, resources: 1 }]);
                  setNewOp({ title: '', est_hours: 0, est_minutes: 0, resources: 1 });
                  setShowAddOp(false);
                }}
                  disabled={!newOp.title.trim()}
                  style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: newOp.title.trim() ? 1 : 0.5 }}>
                  <FiPlus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Add Operation
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddOp(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#2563eb', width: '100%', justifyContent: 'center' }}>
              <FiPlus size={14} /> Add Operation
            </button>
          ))}
        </div>



        {/* File Upload */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
            Documents {files.length > 0 && `(${files.length}/8)`}
          </label>
          <input type="file" ref={fileInputRef} multiple onChange={handleFileChange}
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
            style={{ display: 'none' }}
          />
          <div
            onClick={() => { if (files.length < 8) fileInputRef.current?.click(); }}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = 'rgba(37,99,235,0.06)'; }}
            onDragLeave={e => { e.preventDefault(); e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = ''; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = ''; if (e.dataTransfer.files.length && files.length < 8) { const newFiles = Array.from(e.dataTransfer.files).slice(0, 8 - files.length); setFiles(prev => [...prev, ...newFiles]); } }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px',
              borderRadius: 8, border: '2px dashed var(--border)', background: 'var(--bg-input)',
              cursor: files.length >= 8 ? 'not-allowed' : 'pointer', fontSize: 13,
              color: 'var(--text-secondary)', boxSizing: 'border-box', transition: 'all 0.2s'
            }}>
            <FiUpload size={16} /> Browse Files
          </div>

          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {files.map((file, i) => {
                const isImg = file.type.startsWith('image/');
                const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';
                return (
                  <div key={i} style={{
                    width: 100, borderRadius: 8, overflow: 'hidden',
                    border: '1px solid var(--border)', background: 'var(--bg-input)', position: 'relative'
                  }}>
                    {isImg ? (
                      <img src={URL.createObjectURL(file)} alt={file.name}
                        style={{ width: '100%', height: 70, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{
                        width: '100%', height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'var(--bg-card)', flexDirection: 'column', gap: 2
                      }}>
                        <FiUpload size={16} color="var(--text-muted)" />
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>{ext}</span>
                      </div>
                    )}
                    <div style={{ padding: '4px 6px' }}>
                      <div style={{
                        fontSize: 10, color: 'var(--text-secondary)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>{file.name}</div>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} style={{
                      position: 'absolute', top: 3, right: 3, background: 'rgba(0,0,0,0.5)',
                      border: 'none', cursor: 'pointer', color: '#fff', borderRadius: '50%',
                      width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                    }}><FiX size={10} /></button>
                  </div>
                );
              })}
            </div>
          )}

          {saving && <UploadProgress files={files} progress={uploadProgress} uploading={true} />}
        </div>

      </div>
        {/* Submit - fixed at bottom */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button type="button" onClick={() => navigate(-1)} style={{
            padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 500
          }}>Cancel</button>
          <motion.button type="submit" disabled={saving || !form.name.trim() || !form.asset_id || !dateRange}
            whileHover={(!saving && form.name.trim() && form.asset_id && dateRange) ? { scale: 1.02 } : {}} whileTap={(!saving && form.name.trim() && form.asset_id && dateRange) ? { scale: 0.98 } : {}}
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: (!form.name.trim() || !form.asset_id || !dateRange) ? '#94a3b8' : '#2563eb', color: '#fff',
              cursor: (saving || !form.name.trim() || !form.asset_id || !dateRange) ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
              opacity: (saving || !form.name.trim() || !form.asset_id || !dateRange) ? 0.6 : 1
            }}>{saving ? (files.length > 0 && uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Submitting…') : 'Submit Notification'}</motion.button>
        </div>
      </form>
      </motion.div>
    </motion.div>
  );
}
